import dayjs from 'dayjs';
import { fetchUsers } from '../../../../services/usersServices';
import { fetchAllUsersWeeklyHoursMap, fetchAllUsersWeeklyHoursMapOld } from './utils';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';

// Add plugins to dayjs
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const generateCSVData = async (timesheetData, startDate, endDate) => {
  if (!timesheetData || timesheetData.length === 0) {
    return '';
  }
  const usersData = await fetchUsers();
  const userHoursMap = {};
  const dateHeadersSet = new Set();

  // Normalize start and end dates
  const normalizedStartDate = dayjs(startDate, 'YYYY-MM-DD').startOf('day');
  const normalizedEndDate = dayjs(endDate, 'YYYY-MM-DD').endOf('day');

  timesheetData
    .filter((entry) => {
      const entryDate = dayjs(entry.timeEntryDate, 'DD-MM-YYYY');

      if (!entryDate.isValid()) {
        console.error('Invalid entry date:', entry.timeEntryDate);
        return false;
      }

      const normalizedEntryDate = entryDate.startOf('day');

      // Check if entry date is in range
      return (
        normalizedEntryDate.isSameOrAfter(normalizedStartDate) &&
        normalizedEntryDate.isSameOrBefore(normalizedEndDate)
      );
    })
    .forEach((entry) => {
      const user = usersData.find((user) => user.email === entry.userEmail);
      if (!user) return;

      const fullName = `${user.firstName} ${user.surname}`;
      if (!userHoursMap[fullName]) {
        userHoursMap[fullName] = {};
      }

      const formattedDate = dayjs(entry.timeEntryDate, 'DD-MM-YYYY').format('dddd, MMMM D YYYY');
      dateHeadersSet.add(formattedDate);

      userHoursMap[fullName][formattedDate] =
        (userHoursMap[fullName][formattedDate] || 0) + parseFloat(entry.billableHours || 0);
    });

  // Sort date headers
  const sortedDateHeaders = Array.from(dateHeadersSet).sort((a, b) => {
    return dayjs(a, 'dddd, MMMM D YYYY') - dayjs(b, 'dddd, MMMM D YYYY');
  });

  // Split dates into weekly chunks
  const weeks = [];
  let currentWeek = [];
  sortedDateHeaders.forEach((date, index) => {
    currentWeek.push(date);
    if ((index + 1) % 7 === 0 || index === sortedDateHeaders.length - 1) {
      weeks.push(currentWeek);
      currentWeek = [];
    }
  });

  // Build CSV headers and rows
  const headers = ['Date', ...Object.keys(userHoursMap)];
  const rows = [];

  weeks.forEach((week) => {
    let weeklyTotal = {};
    week.forEach((date) => {
      const row = [`"${date}"`];
      Object.keys(userHoursMap).forEach((user) => {
        const hours = userHoursMap[user][date] || 0;
        row.push(hours.toFixed(2));
        weeklyTotal[user] = (weeklyTotal[user] || 0) + hours;
      });
      rows.push(row.join(','));
    });

    // Add weekly total row
    const totalRow = ['Weekly Total'];
    Object.keys(userHoursMap).forEach((user) => {
      totalRow.push((weeklyTotal[user] || 0).toFixed(2));
    });
    rows.push(totalRow.join(','));
  });

  return [headers.join(','), ...rows].join('\n');
};

export const downloadOvertimeCsv = async (startDate, endDate) => {
  const timesheetData = await fetchAllUsersWeeklyHoursMap(startDate, endDate);
  const csv = await generateCSVData(timesheetData, startDate, endDate);
  if (!csv) {
    alert('No data available to export.');
    return;
  }

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', 'hours-worked-by-user-by-day.csv');
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
